import React, {useState} from "react";
import {
    ActionType,
    Column,
    ColumnType,
    confirmBox,
    KButton,
    KBUTTON_SIZE,
    KBUTTON_VARIANT,
    KCard,
    KContainer,
    KInput,
    KModal,
    KModalSize,
    KModalVariant,
    KSpace,
    KSpinner,
    KSpinnerSize,
    KTableLoader,
    Table
} from "@kopjra/uikit";
import {I18n, Translate} from "react-redux-i18n";
import {Credential, FullCredential} from "../types/users";
import {Col, Row} from "react-bootstrap";
import {Routes} from "../utils/router";
import {copyToClipboard, showAlert} from "../utils/commons";
import {BuyCTA} from "./helpers/BuyCTA";
import {EmptyCreate} from "./helpers/EmptyCreate";

export interface StateProps {
    credentials?: Credential[];
    generatedCredential?: FullCredential;
    creating: boolean;
    isAllowed: boolean;
}

export interface DispatchProps {
    onGetCredentials: () => Promise<void>;
    onDeleteCredential: (id: string) => Promise<void>;
    onCreateCredential: () => Promise<void>;
    onCleanCredential: () => void;
    onGoToStore: () => void;
}

export interface InnerProps {
}

export type Props = StateProps & DispatchProps & InnerProps;

const tableName = "apiKeys_table";

export const ApiKeys: React.FC<Props> = ({onGoToStore, isAllowed, onCleanCredential, creating, generatedCredential, onCreateCredential, onDeleteCredential, onGetCredentials, credentials}) => {
    const [showError, setShowError] = useState(false);

    const actions: ActionType<Credential>[] = [
        {
            name: <div className="nowrap"><i className="fal fa-copy"/> <Translate value="input.copy"/></div>,
            handler: async (datum) => {
                if (datum.id) {
                    copyToClipboard(datum.id);
                    showAlert(I18n.t("input.copied"), "success");
                }
            }
        },
        {
            name: <div className="nowrap"><i className="fal fa-trash"/> <Translate value="generic.delete"/></div>,
            handler: async (datum) => {
                if (await confirmBox({
                        message: I18n.t("user.apiKeys.delete"), yesText: I18n.t("generic.delete"), noText: I18n.t("generic.cancel")
                    })) {
                    await onDeleteCredential(datum.id); await onGetCredentials(); setShowError(false);
                }
            },
        }
    ];

    function onHide() {
        onCleanCredential();
        setShowError(false);
    }

    return (
        <KContainer className="text-center">
            {(credentials) ? (credentials.length) ? (
                <KCard header={<Translate value="user.apiKeys.title"/>}>
                    <Row>
                        <Col className="text-center">
                            <p className="" style={{lineHeight: "35px"}}>
                                {<Translate value="user.apiKeys.help" dangerousHTML={true}/>}
                            </p>
                        </Col>
                    </Row>
                    <KSpace spaces={1}/>
                    {showError ? (
                        <div className="overTableError"><Translate value="user.apiKeys.error"/></div>
                    ) : null}
                    <Table
                        id={tableName}
                        data={credentials}
                        total_count={credentials.length}
                        loaderFunc={async () => onGetCredentials()}
                        keyField={"id"}
                        waiter={<KSpinner size={KSpinnerSize.xl}/>}
                        hidePager={true}
                        hideColumnSelector={true}
                        noMinHeight={true}
                    >
                        <Column colid={"id"} type={ColumnType.GENERIC} name="ID" sort="id" colspan={9} classes="text-start break-all"/>
                        <Column colid="actions" type={ColumnType.ACTIONS} actions={actions} colspan={3} classes="text-end"/>
                    </Table>
                    <KSpace spaces={4}/>
                    <Row>
                        <Col md={4} lg={3}>
                            <KButton
                                text={creating ? <i className="fal fa-spin fa-spinner-third"/> : <><i className="fal fa-key"/> <Translate value="user.apiKeys.create"/></>}
                                disabled={creating}
                                variant={KBUTTON_VARIANT.primary}
                                size={KBUTTON_SIZE.nm}
                                onClick={async () => {
                                    if (credentials.length === 2) {
                                        setShowError(true);
                                    } else {
                                        await onCreateCredential();
                                    }
                                }}
                                fill={true}
                            />
                        </Col>
                    </Row>
                    <KSpace spaces={2}/>
                    <Row>
                        <h4><a className="link" rel="noopener noreferrer" target="_blank" href={Routes.DOCS}><Translate value="user.documentation"/></a></h4>
                    </Row>
                </KCard>
            ) : isAllowed ? (
                <EmptyCreate
                    onCreateSomething={() => onCreateCredential()}
                    createText={<><i className="fal fa-key"/> <Translate value="user.apiKeys.create"/></>}
                    creating={creating}
                    emptyText={<Translate value="user.apiKeys.toStart"/>}
                />
            ) : (
                <BuyCTA/>
            ) : (
                <KCard header={<Translate value="user.apiKeys.title"/>}>
                    <KTableLoader/>
                </KCard>
            )}
            <KModal show={!!generatedCredential} header={<Translate value="user.apiKeys.new"/>}
                    onHide={onHide} variant={KModalVariant.secondary} size={KModalSize.xl} buttonText={<Translate value="generic.ok"/>}>
                {generatedCredential ? (
                    <>
                        <Row>
                            <Col>
                                <KInput id="keyId" value={generatedCredential.id} readOnly={true} copyButton={true} label={<Translate value="user.apiKeys.id"/>}/>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <KInput id="keySecret" value={generatedCredential.secret} readOnly={true} copyButton={true} label={<Translate value="user.apiKeys.secret"/>}/>
                            </Col>
                        </Row>
                        <Row style={{marginTop: -15}}>
                            <Col className="text-left">
                                <p className="small"><Translate value="user.apiKeys.warning"/></p>
                            </Col>
                        </Row>
                    </>
                ) : null}
            </KModal>
        </KContainer>
    );
}
